import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import GlassCard from '../components/UI/GlassCard';
import CyberButton from '../components/UI/CyberButton';
import HUDElement from '../components/UI/HUDElement';
import NeonText from '../components/UI/NeonText';
import ProjectShowcase from '../components/Sections/ProjectShowcase';
import { useScrollAnimation, scrollVariants } from '../hooks/useScrollAnimation';

const Portfolio = () => {
    const [activeFilter, setActiveFilter] = useState('all');
    const { ref: heroRef, controls: heroControls } = useScrollAnimation();

    const filters = [
        { id: 'all', label: 'Todos los Proyectos' },
        { id: 'ai', label: 'Inteligencia Artificial' },
        { id: 'cybersecurity', label: 'Ciberseguridad' },
        { id: 'blockchain', label: 'Blockchain' },
        { id: 'vr', label: 'Realidad Virtual' },
        { id: 'iot', label: 'Internet de las Cosas' }
    ];

    const portfolioStats = [
        { value: 45, label: 'Proyectos de IA', suffix: '+' },
        { value: 28, label: 'Sistemas Seguros', suffix: '+' },
        { value: 17, label: 'Plataformas Blockchain', suffix: '+' },
        { value: 32, label: 'Experiencias Inmersivas', suffix: '+' }
    ];

    const testimonials = [
        {
            id: 1,
            name: 'Dr. Akari Tanaka',
            position: 'CTO, NeuroTech Inc.',
            content: 'La implementación del sistema de reconocimiento neural superó todas nuestras expectativas. La precisión y eficiencia son extraordinarias.',
            project: 'Sistema de Reconocimiento Neural',
            rating: 5
        },
        {
            id: 2,
            name: 'Marcus Johnson',
            position: 'Security Director, Quantum Corp',
            content: 'La solución de ciberseguridad desarrollada ha protegido nuestra infraestructura crítica de múltiples amenazas de nivel avanzado.',
            project: 'Quantum Encryption Network',
            rating: 5
        },
        {
            id: 3,
            name: 'Elena Rodriguez',
            position: 'Lead Researcher, BioLink Labs',
            content: 'La interfaz neuro-digital ha revolucionado nuestra investigación en interfaces cerebro-computadora. Un trabajo excepcional.',
            project: 'Neuro-Link Interface',
            rating: 5
        }
    ];

    return (
        <div className="portfolio-page">
            {/* Hero Section */}
            <motion.section
                className="page-hero"
                ref={heroRef}
                initial="hidden"
                animate={heroControls}
                variants={scrollVariants}
            >
                <div className="container">
                    <div className="hero-content">
                        <NeonText variant="magenta" size="large" animated={true}>
                            // PORTAFOLIO_PROYECTOS
                        </NeonText>
                        <h1 className="hero-title">
                            Innovación en
                            <span className="accent"> Acción</span>
                        </h1>
                        <p className="hero-description">
                            Explora nuestra galería de proyectos tecnológicos avanzados.
                            Cada implementación representa un avance en la frontera digital
                            y una solución a desafíos complejos.
                        </p>

                        <div className="hero-stats">
                            {portfolioStats.map((stat, index) => (
                                <motion.div
                                    key={stat.label}
                                    className="stat-item"
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 + 0.5 }}
                                >
                                    <HUDElement
                                        label={stat.label}
                                        value={stat.value}
                                        unit={stat.suffix}
                                        variant="compact"
                                    />
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* Project Showcase */}
            <section className="section">
                <div className="container">
                    <motion.div
                        className="section-header"
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <NeonText variant="cyan" size="large">
                            // PROYECTOS_DESTACADOS
                        </NeonText>
                        <h2>Implementaciones Recientes</h2>
                        <p>Soluciones tecnológicas que están definiendo el futuro digital</p>
                    </motion.div>

                    <ProjectShowcase />
                </div>
            </section>

            {/* Project Filters */}
            <section className="section filters-section">
                <div className="container">
                    <motion.div
                        className="section-header"
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <NeonText variant="magenta" size="large">
                            // GALERIA_PROYECTOS
                        </NeonText>
                        <h2>Explorar por Categoría</h2>
                    </motion.div>

                    <div className="filters-container">
                        {filters.map((filter) => (
                            <CyberButton
                                key={filter.id}
                                variant={activeFilter === filter.id ? 'cyan' : 'outline'}
                                size="medium"
                                onClick={() => setActiveFilter(filter.id)}
                                className="filter-button"
                            >
                                {filter.label}
                            </CyberButton>
                        ))}
                    </div>

                    <AnimatePresence>
                        <motion.div
                            key={activeFilter}
                            className="projects-grid"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            {/* Aquí iría la lista filtrada de proyectos */}
                            <div className="projects-placeholder">
                                <NeonText variant="cyan">
                                    Mostrando proyectos de: {filters.find(f => f.id === activeFilter)?.label}
                                </NeonText>
                                <p>Los proyectos se cargarán según el filtro seleccionado</p>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </section>

            {/* Testimonials Section */}
            <section className="section testimonials-section">
                <div className="container">
                    <motion.div
                        className="section-header"
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <NeonText variant="cyan" size="large">
                            // TESTIMONIOS_CLIENTES
                        </NeonText>
                        <h2>Lo que Dicen Nuestros Clientes</h2>
                        <p>Feedback de organizaciones que confiaron en nuestras soluciones</p>
                    </motion.div>

                    <div className="testimonials-grid">
                        {testimonials.map((testimonial, index) => (
                            <motion.div
                                key={testimonial.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.2 }}
                            >
                                <GlassCard className="testimonial-card">
                                    <div className="testimonial-header">
                                        <div className="client-info">
                                            <h4>{testimonial.name}</h4>
                                            <p>{testimonial.position}</p>
                                        </div>
                                        <div className="project-badge">
                                            {testimonial.project}
                                        </div>
                                    </div>
                                    <p className="testimonial-content">"{testimonial.content}"</p>
                                    <div className="testimonial-rating">
                                        {'★'.repeat(testimonial.rating)}
                                    </div>
                                </GlassCard>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="section cta-section">
                <div className="container">
                    <GlassCard className="cta-card">
                        <div className="cta-content">
                            <NeonText variant="magenta" size="large">
                                // INICIAR_PROYECTO
                            </NeonText>
                            <h2>¿Tienes un Proyecto en Mente?</h2>
                            <p>
                                Transformemos tu visión en una solución tecnológica real.
                                Nuestro equipo está listo para enfrentar desafíos complejos
                                y entregar resultados excepcionales.
                            </p>
                            <div className="cta-actions">
                                <CyberButton variant="cyan" size="large" to="/contact">
                                    Discutir Proyecto
                                </CyberButton>
                                <CyberButton variant="outline" size="large">
                                    Ver Más Proyectos
                                </CyberButton>
                            </div>
                        </div>
                    </GlassCard>
                </div>
            </section>
        </div>
    );
};

export default Portfolio;